import { TradeValidity } from "../models/TradeValidity";
import { TradingRepo } from "../trading.repo";
import { tradeSheetGenerationRequest } from "../models/tradeSheetGenerationRequest";
import { Locality } from "../../cmodel/cmodel.repo";

class TradeValidityModalController implements angular.IOnInit {
  public tradeSetSecurities;

  public buyTradeValidities: TradeValidity[];
  public sellTradeValidities: TradeValidity[];
  public isLoading: boolean;
  public allSellDatesGTC: Date;
  public allBuyDatesGTC: Date;

  /** @ngInject */
  constructor(
    private $mdDialog: angular.material.IDialogService,
    private tradingRepo: TradingRepo,
    private notifyjs,
    private $window: angular.IWindowService
  ) {}

  $onInit(): void {
    this.buyTradeValidities = this.tradeSetSecurities.buyTrades.map(
      (buyTrade) => new TradeValidity(buyTrade)
    );
    this.sellTradeValidities = this.tradeSetSecurities.sellTrades.map(
      (sellTrade) => new TradeValidity(sellTrade)
    );
  }

  showTickerInformation() {
    return (
      this.buyTradeValidities.some(
        (tv) => tv.locality === Locality.International
      ) ||
      this.sellTradeValidities.some(
        (tv) => tv.locality === Locality.International
      )
    );
  }

  private convertToSecurityTradeDetails(tradeValidity: TradeValidity) {
    if (tradeValidity.validTill) {
      const actualDate = new Date(
        Date.UTC(
          tradeValidity.validTill.getFullYear(),
          tradeValidity.validTill.getMonth(),
          tradeValidity.validTill.getDate()
        )
      );
      tradeValidity.validTill = actualDate;
    }

    return {
      security: tradeValidity.security,
      price: tradeValidity.price,
      indicativePrice: tradeValidity.indicativePrice,
      ticker: tradeValidity.ticker,
      validTill: tradeValidity.validTill,
      useCase: tradeValidity.useCase,
    };
  }

  private shouldGenerateLocalTradeSheet() {
    return (
      this.buyTradeValidities.some((tv) => tv.locality === Locality.Local) ||
      this.sellTradeValidities.some((tv) => tv.locality === Locality.Local)
    );
  }

  private shouldGenerateInternationalTradeSheet() {
    return (
      this.buyTradeValidities.some(
        (tv) => tv.locality === Locality.International
      ) ||
      this.sellTradeValidities.some(
        (tv) => tv.locality === Locality.International
      )
    );
  }

  generateTradeSheet() {
    this.isLoading = true;

    const generationRequests = [];
    if (this.shouldGenerateLocalTradeSheet()) {
      const buyTrades = this.buyTradeValidities.filter(
        (t) => t.locality === Locality.Local
      );
      const sellTrades = this.sellTradeValidities.filter(
        (t) => t.locality === Locality.Local
      );
      const tradePortfolios = new Set(
        buyTrades
          .concat(sellTrades)
          .reduce((prev, t) => [...prev, ...t.portfolios], [])
      );

      generationRequests.push(
        this.tradingRepo.generateTradeSheetWithTradeDetails({
          tradeSetIds: this.tradeSetSecurities.tradeSets
            .filter((ts) => tradePortfolios.has(ts.portfolio))
            .map((t) => t.tradeSetId),
          locality: Locality.Local,
          buySecurityTradeDetails: buyTrades.map(
            this.convertToSecurityTradeDetails
          ),
          sellSecurityTradeDetails: sellTrades.map(
            this.convertToSecurityTradeDetails
          ),
        })
      );
    }

    if (this.shouldGenerateInternationalTradeSheet()) {
      const buyTrades = this.buyTradeValidities.filter(
        (t) => t.locality === Locality.International
      );
      const sellTrades = this.sellTradeValidities.filter(
        (t) => t.locality === Locality.International
      );
      const tradePortfolios = new Set(
        buyTrades
          .concat(sellTrades)
          .reduce((prev, t) => [...prev, ...t.portfolios], [])
      );

      if (
        this.tradeSetSecurities.tradeSets.some(
          (ts) => ts.accountSource === "MCB"
        )
      ) {
        const mcbTradesets = this.tradeSetSecurities.tradeSets.filter(
          (ts) => ts.accountSource === "MCB"
        );
        const mcbPortfolios = mcbTradesets.map((t) => t.portfolio);

        generationRequests.push(
          this.tradingRepo.generateTradeSheetWithTradeDetails({
            tradeSetIds: mcbTradesets
              .filter((t) => tradePortfolios.has(t.portfolio))
              .map((t) => t.tradeSetId),
            locality: Locality.International,
            buySecurityTradeDetails: buyTrades
              .filter(
                (t) => !!t.portfolios.find((p) => mcbPortfolios.includes(p))
              )
              .map(this.convertToSecurityTradeDetails),
            sellSecurityTradeDetails: sellTrades
              .filter(
                (t) => !!t.portfolios.find((p) => mcbPortfolios.includes(p))
              )
              .map(this.convertToSecurityTradeDetails),
          })
        );
      }

      if (
        this.tradeSetSecurities.tradeSets.some(
          (ts) => ts.accountSource === "Rothschild"
        )
      ) {
        const edrTradesets = this.tradeSetSecurities.tradeSets.filter(
          (ts) => ts.accountSource === "Rothschild"
        );
        const edrPortfolios = edrTradesets.map((t) => t.portfolio);

        generationRequests.push(
          this.tradingRepo.generateTradeSheetWithTradeDetails({
            tradeSetIds: edrTradesets
              .filter((t) => tradePortfolios.has(t.portfolio))
              .map((t) => t.tradeSetId),
            locality: Locality.International,
            buySecurityTradeDetails: buyTrades
              .filter(
                (t) => !!t.portfolios.find((p) => edrPortfolios.includes(p))
              )
              .map(this.convertToSecurityTradeDetails),
            sellSecurityTradeDetails: sellTrades
              .filter(
                (t) => !!t.portfolios.find((p) => edrPortfolios.includes(p))
              )
              .map(this.convertToSecurityTradeDetails),
          })
        );
      }
    }

    Promise.all(generationRequests)
      .then((tradeSheetFiles) => {
        tradeSheetFiles.forEach((tradeSheets) => {
          tradeSheets.forEach((tradeSheet) => {
            this.$window.open(tradeSheet.url, "_blank");
          });

          return tradeSheetFiles[0].map((t) => t.url);
        });
      })
      .then((tradeFileUrls) => this.$mdDialog.hide(tradeFileUrls))
      .then(() => (this.isLoading = false))
      .catch((err) => {
        this.notifyjs(err.data);
        this.isLoading = false;
      });
  }

  closeDialog() {
    this.$mdDialog.cancel();
  }

  setAllBuyDates() {
    this.buyTradeValidities.forEach((element) => {
      element.validTill = this.allBuyDatesGTC;
    });
  }
  setAllSellDates() {
    this.sellTradeValidities.forEach((element) => {
      element.validTill = this.allSellDatesGTC;
    });
  }
}

export const tradeValidity: angular.IComponentOptions = {
  template: require("./tradeValidity.modal.html"),
  controller: TradeValidityModalController,
  bindings: {
    tradeSetSecurities: "<",
  },
};



// WEBPACK FOOTER //
// ./src/app/trading/portfoliotradesets/tradeValidity.modal.ts